// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Tracks open remote connections (Web view / RDP / SSH) so AuthContext can
 * treat them as user activity. Mouse/keyboard events inside iframes never
 * reach the parent document, so without this the 20-minute idle timer logs
 * users out while they are actively using a remote session.
 *
 * LEASED holds (2026-07-27 incident): holds used to be bare reason strings in
 * localStorage, cleared only by React effect cleanup or logout — closing the
 * TAB with a viewer open stranded the hold forever, permanently disarming the
 * inactivity logout for every future session in that browser. Now each hold
 * is its own localStorage key (`sf_remote_hold:<reason>` -> timestamp) that
 * the holder component re-stamps every REMOTE_HOLD_RENEW_MS; a hold older
 * than REMOTE_HOLD_TTL_MS is ignored (and pruned on the next write). Stranded
 * holds therefore self-expire within ~2 minutes.
 *
 * Deliberate semantics:
 *  - Per-reason keys: renewals are single-key writes, so two holder tabs can
 *    never clobber each other's stamps (no read-modify-write on a shared map).
 *  - TTL (120s) = 4x the renew interval; Chrome's hidden-tab throttling
 *    floors intervals at ~1/min, still inside the lease. A FROZEN tab
 *    (memory saver / sleeping tabs) stops renewing and its hold expires —
 *    correct: nobody is using that viewer.
 *  - Sleeping the machine >TTL with a viewer open expires the hold, so a
 *    >20-min sleep now ends in the normal inactivity logout. CHOSEN behavior.
 *  - The legacy array-format key is deleted on any write and never honored:
 *    legacy holds carry no lease and are exactly the stranded-flag bug.
 *    Rollout note: during a mixed-version window an OLD-bundle tab's holds
 *    are invisible to NEW-bundle tabs (one-time cost of the kill switch).
 */

export const REMOTE_HOLD_PREFIX = 'sf_remote_hold:';
export const LEGACY_REMOTE_SESSION_KEY = 'sf_remote_session_active';
export const REMOTE_HOLD_RENEW_MS = 30 * 1000;
export const REMOTE_HOLD_TTL_MS = 2 * 60 * 1000;

function allHoldKeys() {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(REMOTE_HOLD_PREFIX)) keys.push(k);
    }
  } catch (_) {
    /* private mode / quota */
  }
  return keys;
}

/** Reasons whose lease is still live. Pure read — never writes (this runs on
 * AuthContext's 60s poll AND the 1Hz session-warning tick). */
function liveReasons(nowMs) {
  const out = [];
  for (const k of allHoldKeys()) {
    let ts = NaN;
    try {
      ts = Number(localStorage.getItem(k));
    } catch (_) {
      /* ignore */
    }
    if (Number.isFinite(ts) && nowMs - ts <= REMOTE_HOLD_TTL_MS) {
      out.push(k.slice(REMOTE_HOLD_PREFIX.length));
    }
  }
  return out;
}

function dispatchActivity(reasons) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('remote-session-activity', {
        detail: { active: reasons.length > 0, reasons },
      })
    );
  }
}

/** Write-time housekeeping: drop expired holds and the legacy-format key. */
function pruneOnWrite(nowMs) {
  try {
    localStorage.removeItem(LEGACY_REMOTE_SESSION_KEY);
    for (const k of allHoldKeys()) {
      const ts = Number(localStorage.getItem(k));
      if (!Number.isFinite(ts) || nowMs - ts > REMOTE_HOLD_TTL_MS) {
        localStorage.removeItem(k);
      }
    }
  } catch (_) {
    /* ignore */
  }
}

/** Mark a remote viewer as open. `reason` should be unique per instance
 * (e.g. http-view:http-2). Holders MUST also renew on an interval of
 * REMOTE_HOLD_RENEW_MS or the hold expires. */
export function markRemoteSessionActive(reason) {
  if (!reason) return;
  const now = Date.now();
  try {
    localStorage.setItem(REMOTE_HOLD_PREFIX + reason, String(now));
  } catch (_) {
    /* ignore */
  }
  pruneOnWrite(now);
  dispatchActivity(liveReasons(now));
}

/** Re-stamp a hold's lease — same write as marking; named for intent. */
export const renewRemoteSessionHold = markRemoteSessionActive;

/** Clear one remote viewer's hold. */
export function markRemoteSessionInactive(reason) {
  if (!reason) return;
  try {
    localStorage.removeItem(REMOTE_HOLD_PREFIX + reason);
  } catch (_) {
    /* ignore */
  }
  const now = Date.now();
  pruneOnWrite(now);
  dispatchActivity(liveReasons(now));
}

export function isRemoteSessionActive() {
  return liveReasons(Date.now()).length > 0;
}

/** Drop all remote-session holds (e.g. on logout). */
export function clearRemoteSessionActivity() {
  try {
    localStorage.removeItem(LEGACY_REMOTE_SESSION_KEY);
    for (const k of allHoldKeys()) localStorage.removeItem(k);
  } catch (_) {
    /* ignore */
  }
  dispatchActivity([]);
}

if (typeof window !== 'undefined') {
  window.addEventListener('user-logout', () => {
    clearRemoteSessionActivity();
  });
}
